import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { clientService, Client, CreateClientRequest } from '../services/clientService';

import { SearchIcon, PlusIcon, ArrowLeftIcon, PencilIcon, TrashIcon } from '../components/Icons';

interface Strategy {
  id: string;
  name: string;
  module: string;
  status: string;
  progress: number;
  created_at: string;
  description: string;
}

const statusLabels: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  in_progress: 'En progreso',
};

const getStatusStyle = (status: string) => {
  switch (status) {
    case 'active': return { bg: 'var(--accent-light)', color: 'var(--accent-text)' };
    case 'in_progress': return { bg: 'rgba(217, 119, 6, 0.1)', color: 'var(--warning)' };
    case 'inactive': return { bg: 'rgba(220, 38, 38, 0.08)', color: 'var(--error)' };
    default: return { bg: 'var(--bg-hover)', color: 'var(--text-secondary)' };
  }
};

const getModuleColor = (module: string) => {
  if (module.includes('Módulo 1')) return 'var(--error)';
  if (module.includes('Módulo 2')) return 'var(--warning)';
  if (module.includes('Módulo 3')) return '#6366f1';
  if (module.includes('Módulo 4')) return 'var(--success)';
  if (module.includes('Módulo 5')) return '#8b5cf6';
  if (module.includes('Módulo 6')) return '#06b6d4';
  if (module.includes('Módulo X')) return '#ec4899';
  if (module.includes('Módulo Y')) return '#f97316';
  if (module.includes('Módulo Z')) return '#3b82f6';
  if (module.includes('Módulo 6-B')) return '#10b981';
  return 'var(--text-secondary)';
};

const Clients: React.FC = () => {
  const navigate = useNavigate();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(8);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateClientRequest>({
    name: '',
    industry: '',
    contact_name: '',
    contact_email: '',
    status: 'active',
  });

  const [activeTab, setActiveTab] = useState<'info' | 'strategies'>('info');
  const [clientStrategies, setClientStrategies] = useState<Strategy[]>([]);
  const [showNewStrategy, setShowNewStrategy] = useState(false);
  const [newStrategy, setNewStrategy] = useState({
    name: '',
    module: 'Módulo 1: El Problema',
    description: '',
  });
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [sortColumn, setSortColumn] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string; name: string }>({
    open: false,
    id: '',
    name: '',
  });

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await clientService.list(page, limit, search, sortColumn, sortOrder);
      setClients(resp.data);
      setTotalPages(resp.total_pages);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, sortColumn, sortOrder]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchClients();
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleDelete = (id: string, name: string) => {
    setDeleteDialog({ open: true, id, name });
  };

  const confirmDelete = async () => {
    try {
      await clientService.delete(deleteDialog.id);
      setDeleteDialog({ open: false, id: '', name: '' });
      fetchClients();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await clientService.update(editingId, formData);
        setShowForm(false);
        setEditingId(null);
        setFormData({ name: '', industry: '', contact_name: '', contact_email: '', status: 'active' });
        setActiveTab('info');
        setClientStrategies([]);
        fetchClients();
      } else {
        const newClient = await clientService.create(formData);
        navigate(`/clients/${newClient.id}`);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al guardar');
    }
  };

  const handleNew = () => {
    setEditingId(null);
    setFormData({ name: '', industry: '', contact_name: '', contact_email: '', status: 'active' });
    setActiveTab('info');
    setClientStrategies([]);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', industry: '', contact_name: '', contact_email: '', status: 'active' });
    setActiveTab('info');
    setClientStrategies([]);
    setShowNewStrategy(false);
  };

  const handleAddStrategy = () => {
    if (!newStrategy.name.trim() || !newStrategy.description.trim()) {
      alert('Completa el nombre y la descripción de la estrategia');
      return;
    }
    const strategy: Strategy = {
      id: Date.now().toString(),
      name: newStrategy.name,
      module: newStrategy.module,
      status: 'En progreso',
      progress: 0,
      created_at: new Date().toISOString().split('T')[0],
      description: newStrategy.description,
    };
    setClientStrategies(prev => [...prev, strategy]);
    setNewStrategy({ name: '', module: 'Módulo 1: El Problema', description: '' });
    setShowNewStrategy(false);
  };

  const renderStrategyForm = () => (
    <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
      <h3 style={styles.sectionTitle}>Nueva Estrategia</h3>
      <div style={styles.formGrid}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Nombre *</label>
          <input
            style={styles.input}
            value={newStrategy.name}
            onChange={e => setNewStrategy({ ...newStrategy, name: e.target.value })}
            placeholder="Ej: Campaña Verano 2026"
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Módulo *</label>
          <select
            style={styles.input}
            value={newStrategy.module}
            onChange={e => setNewStrategy({ ...newStrategy, module: e.target.value })}
          >
            <option value="Módulo 1: El Problema">Módulo 1: El Problema</option>
            <option value="Módulo 2: El Insight">Módulo 2: El Insight</option>
            <option value="Módulo 3: Dirección Creativa">Módulo 3: Dirección Creativa</option>
            <option value="Módulo 4: Verdad Activadora">Módulo 4: Verdad Activadora</option>
            <option value="Módulo 5: Estrategia de Integración">Módulo 5: Estrategia de Integración</option>
            <option value="Módulo 6: Accionables">Módulo 6: Accionables</option>
            <option value="Módulo X: Bifurcación">Módulo X: Bifurcación</option>
            <option value="Módulo Y: Subida de Información">Módulo Y: Subida de Información</option>
            <option value="Módulo Z: Diagnóstico">Módulo Z: Diagnóstico</option>
            <option value="Módulo 6-B: Accionables Integrados">Módulo 6-B: Accionables Integrados</option>
          </select>
        </div>
        <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
          <label style={styles.label}>Descripción *</label>
          <textarea
            style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
            value={newStrategy.description}
            onChange={e => setNewStrategy({ ...newStrategy, description: e.target.value })}
            placeholder="Describe el objetivo y alcance de la estrategia..."
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
        <button type="button" onClick={() => setShowNewStrategy(false)} style={styles.cancelBtn}>Cancelar</button>
        <button type="button" onClick={handleAddStrategy} style={styles.saveBtn}>Guardar Estrategia</button>
      </div>
    </div>
  );

  const renderStrategiesList = () => (
    <div>
      {clientStrategies.length === 0 ? (
        <div style={styles.card}>
          <div style={styles.emptyState}>
            <button onClick={() => setShowNewStrategy(true)} style={styles.addStratBtn}>+ Crear primera estrategia</button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.25rem' }}>
            <button onClick={() => setShowNewStrategy(true)} style={styles.addStratBtn}>+ Nueva Estrategia</button>
          </div>
          <div style={styles.strategiesList}>
            {clientStrategies.map((s, index) => (
              <div key={s.id} style={styles.strategyCard}>
                <div style={styles.stratHeader}>
                  <div style={styles.stratNumber}>#{index + 1}</div>
                  <div style={{
                    ...styles.stratStatusBadge,
                    background: s.status === 'Finalizado' ? 'var(--accent-light)' : 'rgba(217, 119, 6, 0.1)',
                    color: s.status === 'Finalizado' ? 'var(--accent-text)' : 'var(--warning)',
                  }}>{s.status}</div>
                </div>
                <h3 style={styles.stratName}>{s.name}</h3>
                <p style={styles.stratDesc}>{s.description}</p>
                <div style={styles.stratMeta}>
                  <div style={styles.stratMetaItem}>
                    <span style={styles.metaLabel}>Módulo</span>
                    <span style={{ ...styles.metaValue, color: getModuleColor(s.module) }}>{s.module}</span>
                  </div>
                  <div style={styles.stratMetaItem}>
                    <span style={styles.metaLabel}>Creada</span>
                    <span style={styles.metaValue}>{s.created_at}</span>
                  </div>
                </div>
                <div style={styles.progressSection}>
                  <div style={styles.progressHeader}>
                    <span style={styles.progressLabel}>Progreso</span>
                    <span style={styles.progressPercent}>{s.progress}%</span>
                  </div>
                  <div style={styles.progressBarBg}>
                    <div style={{
                      ...styles.progressBarFill,
                      width: `${s.progress}%`,
                      background: s.status === 'Finalizado' ? 'var(--success)' : 'var(--accent)',
                    }} />
                  </div>
                </div>
                <div style={styles.stratActions}>
                  <button style={styles.stratActionBtnPrimary}>Continuar →</button>
                  <button style={styles.stratActionBtnSecondary}>Exportar</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <Layout>
      <div>
        {!showForm && (
          <>
            <h1 style={styles.pageTitle}>Clientes</h1>
            <p style={styles.pageSubtitle}>Gestiona tus cuentas y accede a sus estrategias</p>
          </>
        )}

        {showForm ? (
          <div>
            <div style={styles.detailHeader}>
              <h1 style={styles.detailTitle}>{editingId ? formData.name || 'Cliente' : 'Nuevo Cliente'}</h1>
            </div>

            {editingId && (
              <div style={styles.tabs}>
                <button
                  onClick={() => setActiveTab('info')}
                  style={{ ...styles.tab, ...(activeTab === 'info' ? styles.tabActive : {}) }}
                >
                  Información General
                </button>
                <button
                  onClick={() => setActiveTab('strategies')}
                  style={{ ...styles.tab, ...(activeTab === 'strategies' ? styles.tabActive : {}) }}
                >
                  Estrategias ({clientStrategies.length})
                </button>
              </div>
            )}

            {activeTab === 'info' && (
              <div style={styles.card}>
                <h2 style={styles.sectionTitle}>Datos del Cliente</h2>
                <form onSubmit={handleSubmit}>
                  <div style={styles.formGrid}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Nombre del Cliente *</label>
                      <input
                        style={styles.input}
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        required
                        placeholder="Ej: Coca-Cola"
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Industria *</label>
                      <input
                        style={styles.input}
                        value={formData.industry}
                        onChange={e => setFormData({ ...formData, industry: e.target.value })}
                        required
                        placeholder="Ej: Bebidas"
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Nombre de Contacto *</label>
                      <input
                        style={styles.input}
                        value={formData.contact_name}
                        onChange={e => setFormData({ ...formData, contact_name: e.target.value })}
                        required
                        placeholder="Ej: María González"
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Email de Contacto *</label>
                      <input
                        type="email"
                        style={styles.input}
                        value={formData.contact_email}
                        onChange={e => setFormData({ ...formData, contact_email: e.target.value })}
                        required
                        placeholder="Ej: maria@cliente.com"
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Estado *</label>
                      <select
                        style={styles.input}
                        value={formData.status}
                        onChange={e => setFormData({ ...formData, status: e.target.value as any })}
                      >
                        <option value="active">Activo</option>
                        <option value="in_progress">En progreso</option>
                        <option value="inactive">Inactivo</option>
                      </select>
                    </div>
                  </div>
                  <div style={styles.formActions}>
                    <button type="button" onClick={handleCancel} style={styles.cancelBtn}>
                      <ArrowLeftIcon size={16} color="var(--text-secondary)" />
                      <span>Volver al listado</span>
                    </button>
                    <button type="submit" style={styles.saveBtn}>
                      {editingId ? 'Guardar Cambios' : 'Crear Cliente'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'strategies' && editingId && (
              <div>
                {showNewStrategy && renderStrategyForm()}
                {renderStrategiesList()}
                <div style={{ marginTop: '1.5rem' }}>
                  <button onClick={handleCancel} style={styles.cancelBtn}>
                    <ArrowLeftIcon size={16} color="var(--text-secondary)" />
                    <span>Volver al listado</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div style={styles.gridHeader}>
              <form onSubmit={handleSearch} style={styles.searchBar}>
                <SearchIcon size={18} color="var(--text-muted)" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, industria o contacto..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={styles.searchInput}
                />
              </form>
              <div style={styles.gridActions}>
                <button onClick={handleNew} style={styles.addButton}>
                  <PlusIcon size={16} color="#fff" />
                  <span>Nuevo Cliente</span>
                </button>
              </div>
            </div>

            {error && <div style={styles.errorBanner}>{error}</div>}

            <div style={styles.tableContainer}>
              {loading ? (
                <div style={styles.loading}>Cargando...</div>
              ) : (
                <>
                  {isMobile ? (
                    <div style={styles.cardsList}>
                      {clients.map((client) => {
                        const statusStyle = getStatusStyle(client.status);
                        return (
                          <div key={client.id} style={styles.clientCard}>
                            <div style={styles.cardHeader}>
                              <div
                                style={styles.clientNameLink}
                                onClick={() => navigate(`/clients/${client.id}`)}
                              >
                                {client.name}
                              </div>
                              <div style={styles.cardActions}>
                                <button
                                  onClick={() => navigate(`/clients/${client.id}`)}
                                  style={styles.editBtn}
                                  title="Ver detalle"
                                >
                                  <PencilIcon size={14} color="var(--text-secondary)" />
                                </button>
                                <button
                                  onClick={() => handleDelete(client.id, client.name)}
                                  style={styles.deleteBtn}
                                  title="Eliminar"
                                >
                                  <TrashIcon size={14} color="var(--error)" />
                                </button>
                              </div>
                            </div>
                            <div style={styles.cardMeta}>
                              <span style={styles.cardMetaItem}>{client.contact_email}</span>
                              <span style={styles.cardMetaDot}>·</span>
                              <span style={styles.cardMetaItem}>{client.industry}</span>
                              <span style={styles.cardMetaDot}>·</span>
                              <span style={styles.cardMetaItem}>{client.contact_name}</span>
                            </div>
                            <div style={styles.cardFooter}>
                              <span style={{ ...styles.statusBadge, background: statusStyle.bg, color: statusStyle.color }}>
                                {statusLabels[client.status] || client.status}
                              </span>
                              <span
                                style={styles.strategyCountLink}
                                onClick={() => navigate(`/clients/${client.id}?tab=strategies`)}
                              >
                                {client.strategies_count} estrategias
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={styles.tableScroll}>
                      <table style={styles.table}>
                        <thead>
                          <tr style={styles.theadRow}>
                            <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('name')}>Cliente {sortColumn === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                            <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('industry')}>Industria {sortColumn === 'industry' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                            <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('contact_name')}>Contacto {sortColumn === 'contact_name' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                            <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('status')}>Estado {sortColumn === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                            <th style={{ ...styles.th, cursor: 'pointer' }} onClick={() => handleSort('strategies_count')}>Estrategias {sortColumn === 'strategies_count' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                            <th style={{ ...styles.th, width: '140px' }}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clients.map((client) => {
                            const statusStyle = getStatusStyle(client.status);
                            return (
                              <tr key={client.id} style={styles.tr}>
                                <td style={styles.td}>
                                  <div
                                    style={styles.clientNameLink}
                                    onClick={() => navigate(`/clients/${client.id}`)}
                                  >
                                    {client.name}
                                  </div>
                                  <div style={styles.clientEmail}>{client.contact_email}</div>
                                </td>
                                <td style={styles.td}>{client.industry}</td>
                                <td style={styles.td}>{client.contact_name}</td>
                                <td style={styles.td}>
                                  <span style={{ ...styles.statusBadge, background: statusStyle.bg, color: statusStyle.color }}>
                                    {statusLabels[client.status] || client.status}
                                  </span>
                                </td>
                                <td style={styles.td}>
                                  <span
                                    style={styles.strategyCountLink}
                                    onClick={() => navigate(`/clients/${client.id}?tab=strategies`)}
                                  >
                                    {client.strategies_count}
                                  </span>
                                </td>
                                <td style={styles.td}>
                                  <button
                                    onClick={() => navigate(`/clients/${client.id}`)}
                                    style={styles.editBtn}
                                    title="Ver detalle"
                                  >
                                    <PencilIcon size={14} color="var(--text-secondary)" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(client.id, client.name)}
                                    style={styles.deleteBtn}
                                    title="Eliminar"
                                  >
                                    <TrashIcon size={14} color="var(--error)" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {clients.length === 0 && (
                    <div style={styles.emptyState}>
                      <div style={styles.emptyText}>No se encontraron clientes</div>
                    </div>
                  )}

                  {totalPages > 1 && (
                    <div style={styles.pagination}>
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        style={{ ...styles.pageBtn, opacity: page === 1 ? 0.5 : 1 }}
                      >
                        ← Anterior
                      </button>
                      <span style={styles.pageInfo}>Página {page} de {totalPages}</span>
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        style={{ ...styles.pageBtn, opacity: page === totalPages ? 0.5 : 1 }}
                      >
                        Siguiente →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteDialog.open}
        title="Eliminar cliente"
        message={`¿Estás seguro de que quieres eliminar a "${deleteDialog.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialog({ open: false, id: '', name: '' })}
      />
    </Layout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  pageTitle: { fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.25rem' },
  pageSubtitle: { color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '1.5rem' },
  detailHeader: { marginBottom: '1.5rem' },
  detailTitle: { fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.75rem' },
  tabs: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    borderBottom: '2px solid var(--border)',
  },
  tab: {
    padding: '0.85rem 1.5rem',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-muted)',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    borderBottom: '3px solid transparent',
    marginBottom: '-2px',
    transition: 'all 0.2s',
    fontFamily: 'var(--font-body)',
  },
  tabActive: {
    color: 'var(--accent)',
    borderBottomColor: 'var(--accent)',
  },
  card: {
    background: 'var(--bg-card)',
    borderRadius: 'var(--radius)',
    padding: '1.5rem',
    boxShadow: 'var(--shadow)',
    border: '1px solid var(--border)',
  },
  sectionTitle: { fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '1.25rem' },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '1.25rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  input: {
    padding: '0.8rem',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-input)',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'var(--font-body)',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '1.5rem',
    paddingTop: '1.25rem',
    borderTop: '1px solid var(--border)',
  },
  cancelBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
    padding: '0.7rem 1.5rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    transition: 'all 0.2s',
    fontFamily: 'var(--font-body)',
  },
  saveBtn: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.5rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    transition: 'background 0.2s',
    fontFamily: 'var(--font-body)',
  },
  gridHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flex: 1,
    minWidth: '300px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    padding: '0 0.75rem',
  },
  searchInput: {
    flex: 1,
    padding: '0.7rem 0.5rem',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-primary)',
    fontSize: '0.9rem',
    outline: 'none',
    fontFamily: 'var(--font-body)',
  },
  gridActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  addButton: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.25rem',
    borderRadius: 'var(--radius-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '0.9rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontFamily: 'var(--font-body)',
    transition: 'background 0.2s',
  },
  errorBanner: {
    background: 'rgba(220, 38, 38, 0.08)',
    color: 'var(--error)',
    padding: '0.75rem 1rem',
    borderRadius: 'var(--radius-sm)',
    marginBottom: '1rem',
    fontSize: '0.9rem',
    border: '1px solid rgba(220, 38, 38, 0.15)',
  },
  tableContainer: {
    background: 'var(--bg-card)',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow)',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  },
  tableScroll: {
    overflowX: 'auto',
  },
  cardsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    padding: '0.75rem',
  },
  clientCard: {
    background: 'var(--bg-card)',
    borderRadius: 'var(--radius-sm)',
    padding: '1rem',
    border: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardActions: {
    display: 'flex',
    gap: '0.25rem',
  },
  cardMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.35rem',
    fontSize: '0.8rem',
    color: 'var(--text-muted)',
  },
  cardMetaItem: {
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
  },
  cardMetaDot: {
    fontSize: '0.7rem',
    color: 'var(--text-muted)',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '0.25rem',
  },
  loading: {
    padding: '3rem',
    textAlign: 'center',
    color: 'var(--text-muted)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.9rem',
  },
  theadRow: {
    background: 'var(--bg-hover)',
    borderBottom: '2px solid var(--border)',
  },
  th: {
    padding: '1rem',
    textAlign: 'left',
    fontWeight: 700,
    color: 'var(--text-secondary)',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid var(--border-subtle)',
    transition: 'background 0.15s',
  },
  td: {
    padding: '1rem',
    color: 'var(--text-primary)',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  clientNameLink: {
    fontWeight: 600,
    color: 'var(--accent)',
    cursor: 'pointer',
    transition: 'color 0.2s',
    textDecoration: 'underline',
  },
  clientEmail: {
    fontSize: '0.8rem',
    color: 'var(--text-muted)',
    marginTop: '0.15rem',
  },
  statusBadge: {
    padding: '0.35rem 0.75rem',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: 700,
    display: 'inline-block',
  },
  strategyCount: {
    background: 'var(--bg-hover)',
    padding: '0.3rem 0.7rem',
    borderRadius: '6px',
    fontWeight: 700,
    fontSize: '0.85rem',
  },
  strategyCountLink: {
    background: 'var(--bg-hover)',
    padding: '0.3rem 0.7rem',
    borderRadius: '6px',
    fontWeight: 700,
    fontSize: '0.85rem',
    color: 'var(--accent)',
    cursor: 'pointer',
    transition: 'background 0.2s',
    textDecoration: 'underline',
  },
  editBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: '0.4rem',
    borderRadius: '6px',
    transition: 'background 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '0.25rem',
  },
  deleteBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--error)',
    cursor: 'pointer',
    padding: '0.4rem',
    borderRadius: '6px',
    transition: 'background 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    padding: '3rem',
    textAlign: 'center',
    color: 'var(--text-muted)',
  },
  emptyIcon: {
    fontSize: '2.5rem',
    marginBottom: '0.75rem',
  },
  emptyText: {
    fontSize: '1rem',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem',
    borderTop: '1px solid var(--border-subtle)',
  },
  pageBtn: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    padding: '0.5rem 1rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)',
  },
  pageInfo: {
    fontSize: '0.85rem',
    color: 'var(--text-secondary)',
  },
  strategiesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  strategyCard: {
    background: 'var(--bg-card)',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    boxShadow: 'var(--shadow)',
    border: '1px solid var(--border)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  stratHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  stratNumber: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: 'var(--text-muted)',
    background: 'var(--bg-hover)',
    padding: '0.3rem 0.7rem',
    borderRadius: '6px',
  },
  stratStatusBadge: {
    fontSize: '0.8rem',
    fontWeight: 700,
    padding: '0.35rem 0.9rem',
    borderRadius: '20px',
  },
  stratName: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: '0.5rem',
  },
  stratDesc: {
    fontSize: '0.95rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    marginBottom: '1.25rem',
  },
  stratMeta: {
    display: 'flex',
    gap: '2rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
  },
  stratMetaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem',
  },
  metaLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  metaValue: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  progressSection: {
    marginBottom: '1.5rem',
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  progressLabel: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
  },
  progressPercent: {
    fontSize: '1.1rem',
    fontWeight: 800,
    color: 'var(--text-primary)',
  },
  progressBarBg: {
    height: '10px',
    background: 'var(--bg-hover)',
    borderRadius: '5px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: '5px',
    transition: 'width 0.8s ease',
  },
  stratActions: {
    display: 'flex',
    gap: '0.75rem',
  },
  stratActionBtnPrimary: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.5rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    flex: 1,
    fontFamily: 'var(--font-body)',
    transition: 'background 0.2s',
  },
  stratActionBtnSecondary: {
    background: 'var(--bg-hover)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
    padding: '0.7rem 1.5rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    fontFamily: 'var(--font-body)',
    transition: 'all 0.2s',
  },
  addStratBtn: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '0.7rem 1.5rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    fontFamily: 'var(--font-body)',
  },
};

export default Clients;
